import React, { useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, TableWrap, Th, Td, Bars, Segmented, MarketTag, UserLink } from '../components/ui/bits';
import { DailySplit } from '../components/charts/DailySplit';
import { useRpc } from '../lib/queries';
import { fmtPct, agoLabel } from '../lib/format';
import { sentence } from '../lib/labels';
import type { PsychometricsResp } from '../lib/types';

/** Psychometric runs counted in SQL (admin_dash_psychometrics), plus the catalogue health check. */
export const PsychometricsPage: React.FC = () => {
  const [days, setDays] = useState('30');
  const { data: p, error, isLoading } = useRpc<PsychometricsResp>('admin_dash_psychometrics', { p_days: Number(days) });
  const { data: audit, error: auditErr } = useRpc<any[]>('admin_playability_audit');

  if (isLoading) return <LoadingRow label="Loading psychometrics" />;
  if (error || !p) return <ErrorPanel error={error ?? 'No data returned'} label="admin_dash_psychometrics" />;
  const w = p.window;

  return (
    <div>
      <PageHeader
        title="Psychometrics"
        subtitle="Runs of every psychometric test, counted from every row. Internal accounts are excluded."
        actions={<Segmented label="Window" value={days} onChange={setDays} options={[{ value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }]} />}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <StatTile label={`Runs, ${p.days} days`} value={w.runs.toLocaleString('en-GB')} split={{ uk: w.uk, us: w.us }} sub={`${w.users} people`} source="psychometric_test_runs" />
        <StatTile label="Runs, 7 days" value={p.d7.toLocaleString('en-GB')} />
        <StatTile label="Completed" value={fmtPct(w.completed, w.runs, 0)} sub={`${w.abandoned} abandoned`} />
        <StatTile label="Average score" value={w.avg_score ?? 'None'} sub="Completed runs" />
        <StatTile label="All runs" value={p.all_time.toLocaleString('en-GB')} />
        <StatTile label="Active tests" value={p.active_tests} sub={`${p.calibrated_rows} calibrated firm rows`} source="psychometric_tests, firm_test_calibration" />
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Section title="Runs per day" source="psychometric_test_runs, London days">
          <DailySplit data={p.daily} unit="runs" />
        </Section>
        <Section
          title="Catalogue health"
          sub="Active tests that are not games and have no authored items. Fine for procedural tests; any other test listed here is invisible in the catalogue."
          source="admin_playability_audit"
        >
          {auditErr ? <ErrorPanel error={auditErr} /> : (audit ?? []).length === 0 ? <EmptyNote>Every active test has items or is a game</EmptyNote> : (
            <>
              <TableWrap>
                <thead><tr><Th>Slug</Th><Th>Category</Th><Th>Provider</Th><Th className="text-right">Runs</Th></tr></thead>
                <tbody>
                  {(audit ?? []).slice(0, 15).map(a => (
                    <tr key={a.slug}>
                      <Td className="font-mono text-[12px]">{a.slug}</Td>
                      <Td>{a.category}</Td>
                      <Td>{a.provider_style}</Td>
                      <Td className="text-right tabular-nums">{a.runs}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
              <p className="text-[12px] text-stone-500 mt-2">
                {(audit ?? []).length} active tests with no items. Check against PROCEDURAL_TEST_SLUGS in the app when adding a test; the four legacy demo slugs are known and deliberate.
              </p>
            </>
          )}
        </Section>
      </div>

      <div className="grid lg:grid-cols-3 gap-3 mb-3">
        <Section title="By provider style" source="psychometric_tests.provider_style">
          {p.by_provider.length === 0 ? <EmptyNote>No runs</EmptyNote> : <Bars rows={p.by_provider.map(r => ({ key: r.label, label: r.label, n: r.n }))} />}
        </Section>
        <Section title="Most-run tests" sub="High abandonment on one test usually means a usability problem.">
          {p.by_test.length === 0 ? <EmptyNote>No runs</EmptyNote> : (
            <div className="space-y-1.5 text-[12.5px]">
              {p.by_test.map(t => (
                <div key={t.slug} className="flex items-center justify-between gap-2">
                  <span className="truncate text-stone-700">{t.name}</span>
                  <span className="whitespace-nowrap text-stone-600 tabular-nums">
                    {t.n}{t.abandoned > 0 && <Badge tone="amber" className="ml-1.5">{t.abandoned} abandoned</Badge>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Firm-tailored practice" source="psychometric_test_runs.firm_name">
          {p.by_firm.length === 0 ? <EmptyNote>No firm-tailored runs</EmptyNote> : <Bars rows={p.by_firm.map(r => ({ key: r.label, label: r.label, n: r.n }))} />}
        </Section>
      </div>

      <Section title="Latest runs" sub="Newest 25.">
        <TableWrap>
          <thead><tr><Th>Test</Th><Th>Firm</Th><Th>User</Th><Th>Market</Th><Th>Status</Th><Th>Score</Th><Th>When</Th></tr></thead>
          <tbody>
            {p.recent.map(r => (
              <tr key={r.id}>
                <Td>{r.test_name ?? 'Unknown test'}</Td>
                <Td>{r.firm_name ?? ''}</Td>
                <Td><UserLink id={r.user_id} /></Td>
                <Td><MarketTag market={r.market} /></Td>
                <Td><Badge tone={r.status === 'completed' ? 'emerald' : r.status === 'abandoned' ? 'stone' : 'amber'}>{sentence(r.status)}</Badge></Td>
                <Td className="tabular-nums">{r.score ?? ''}</Td>
                <Td className="whitespace-nowrap">{agoLabel(r.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Section>
    </div>
  );
};
